import {
  ReportAggregationType,
  ReportDateHistogramType,
  ReportFilterOperation,
  ReportMetricType,
} from '@driftt/api-types'

import AggregationBuilder from '../query/AggregationBuilder'
import { IQueryConfig, IQueryOptions } from '../query/types'

import QueryBuilder from './ConversationSummaryQueryBuilder'
import * as Parsers from './parsers'

const QueryParsersByMetric = {
  conversationsHeatmap: (query) => ({
    query,
    parseFunction: Parsers.parseHeatmap,
  }),
  repTable: (query) => ({ query, parseFunction: Parsers.parseRepTable }),
  urlTable: (query) => ({ query, parseFunction: Parsers.parseUrlTable }),
  topTags: (query) => ({ query, parseFunction: Parsers.parseTopTagsTable }),
  newConversations: (query) => ({
    query,
    parseFunction: Parsers.parseLineChart,
  }),
  repPerformance: (query) => ({
    query,
    parseFunction: Parsers.parseRepPerformanceTable,
  }),
  liveChatRatingsByRep: (query) => ({
    query,
    parseFunction: Parsers.parseConversationRatingsByRep,
  }),
  medianResponseTime: (query) => ({
    query,
    parseFunction: Parsers.parseMedian,
  }),
  medianConversationLength: (query) => ({
    query,
    parseFunction: Parsers.parseMedian,
  }),
  medianResponseTimeLine: (query) => ({
    query,
    parseFunction: Parsers.parseLineChart,
  }),
  medianConversationLengthLine: (query) => ({
    query,
    parseFunction: Parsers.parseLineChart,
  }),
  routingActionTypes: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTable,
  }),
  routingActionSubTypes: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTable,
  }),
  routingTypeParticipants: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTable,
  }),
  routingSubTypeParticipants: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTable,
  }),
  routingTypePlaybookActionsParticipants: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTable,
  }),
  routingTypePlaybookGoalsParticipants: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTable,
  }),
  routingActionRoutingRules: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTableRules,
  }),
  routingActionPlaybooks: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTablePlaybooks,
  }),
  routingActionPlaybookActions: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTablePlaybookActions,
  }),
  routingActionPlaybookGoals: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTablePlaybookGoals,
  }),
  routingActionRoutingRuleParticipants: (query) => ({
    query,
    parseFunction: Parsers.parseRoutingTable,
  }),
}

const makeQueries = ({
  startDate,
  endDate,
  value,
  keyword,
  sortConfig,
  exportData = false,
  ...queryConfig
}: IQueryConfig & IQueryOptions) => ({
  conversationsHeatmap: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder(
          'dayOfWeek(firstEndUserMessageAt)',
          ReportAggregationType.HISTOGRAM
        )
          .withMinMax(1, 7)
          .withAggregation(
            new AggregationBuilder(
              'hourOfDay(firstEndUserMessageAt)',
              ReportAggregationType.HISTOGRAM
            )
              .withDocCount()
              .withMinMax(0, 23)
              .build()
          )
          .build()
      )
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .build(),
  repTable: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder('firstAgentId', ReportAggregationType.TERMS)
          .withDocCount()
          .withSize(0)
          .build()
      )
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .build(),
  urlTable: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder('pageUrl')
          .withDocCount()
          .withSize(exportData ? 0 : 10)
          .build()
      )
      .withFilterType('pageUrl', keyword)
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .build(),
  topTags: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder('classificationId')
          .withDocCount()
          .withSize(exportData ? 0 : 10)
          .build()
      )
      .withAggregation(
        new AggregationBuilder('tagNames')
          .withDocCount()
          .withSize(exportData ? 0 : 10)
          .build()
      )
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .build(),
  newConversations: () =>
    new QueryBuilder(startDate, endDate)
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .withAggregation(
        new AggregationBuilder(
          'firstEndUserMessageAt',
          ReportAggregationType.DATE_HISTOGRAM
        )
          .withMinMax(startDate, endDate)
          .withInterval(ReportDateHistogramType.DAY)
          .withDocCount()
          .build()
      )
      .build(),
  repPerformance: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder('firstAgentId', ReportAggregationType.TERMS)
          .withDocCount()
          .withMetric(
            'timeToAgentResponseAfterBotMillis',
            ReportMetricType.MEDIAN
          )
          .withMetric('timeToCloseMillis', ReportMetricType.MEDIAN)
          .withMetric('agentAverageResponseMillis', ReportMetricType.AVG)
          .withSize(0)
          .withSort(sortConfig)
          .build()
      )
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .withFilter(
        'timeToAgentResponseAfterBotMillis',
        ReportFilterOperation.HAS_PROPERTY
      )
      .build(),
  liveChatRatingsByRep: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder(
          'latestLiveChatRating.attributedRatingAgentId',
          ReportAggregationType.TERMS
        )
          .withDocCount()
          .withMetric('latestLiveChatRating.ratingValue', ReportMetricType.AVG)
          .withSize(0)
          .build()
      )
      .build(),
  medianResponseTime: () =>
    new QueryBuilder(startDate, endDate)
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .withFilter(
        'timeToAgentResponseAfterBotMillis',
        ReportFilterOperation.HAS_PROPERTY
      )
      .withMetric('timeToAgentResponseAfterBotMillis', ReportMetricType.MEDIAN)
      .build(),
  medianConversationLength: () =>
    new QueryBuilder(startDate, endDate)
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .withFilter('timeToCloseMillis', ReportFilterOperation.HAS_PROPERTY)
      .withMetric('timeToCloseMillis', ReportMetricType.MEDIAN)
      .build(),
  medianResponseTimeLine: () =>
    new QueryBuilder(startDate, endDate)
      .withFilter(
        'timeToAgentResponseAfterBotMillis',
        ReportFilterOperation.HAS_PROPERTY
      )
      .withAggregation(
        new AggregationBuilder(
          'firstEndUserMessageAt',
          ReportAggregationType.DATE_HISTOGRAM
        )
          .withMetric(
            'timeToAgentResponseAfterBotMillis',
            ReportMetricType.MEDIAN
          )
          .withMinMax(startDate, endDate)
          .build()
      )
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .build(),
  medianConversationLengthLine: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder(
          'firstEndUserMessageAt',
          ReportAggregationType.DATE_HISTOGRAM
        )
          .withMetric('timeToCloseMillis', ReportMetricType.MEDIAN)
          .withMinMax(startDate, endDate)
          .build()
      )
      .withFilters(queryConfig)
      .withFilterGroups(queryConfig)
      .build(),
  routingActionTypes: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.type')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingActionSubTypes: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.subType')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .withFilterType('routingType', value)
      .build(),
  routingTypeParticipants: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('routingType', value)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.participants')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingSubTypeParticipants: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('subType', value)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.participants')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingTypePlaybookActionsParticipants: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('routingType', 'PLAYBOOK_ACTION')
      .withFilterType('relatedPlaybookId', value)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.participants')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingTypePlaybookGoalsParticipants: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('goalId', value)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.participants')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingActionRoutingRules: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('routingType', value)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.routingRuleId')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingActionPlaybooks: () =>
    new QueryBuilder(startDate, endDate)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.playbookId')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingActionPlaybookActions: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('routingType', value)
      .withAggregation(
        new AggregationBuilder('relatedPlaybookId')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingActionPlaybookGoals: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('playbookId', value)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.playbookId')
          .withAggregation(
            new AggregationBuilder('firstRoutingAction.playbookGoalId')
              .withDocCount()
              .build()
          )
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),
  routingActionRoutingRuleParticipants: () =>
    new QueryBuilder(startDate, endDate)
      .withFilterType('routingRuleId', value)
      .withAggregation(
        new AggregationBuilder('firstRoutingAction.participants')
          .withDocCount()
          .orderBy(sortConfig!.order)
          .build()
      )
      .build(),

  conversationClassifier: () =>
    new QueryBuilder(startDate, endDate, false)
      .withFilter('createdAt', ReportFilterOperation.BETWEEN, [
        startDate,
        endDate,
      ])
      .withAggregation(
        new AggregationBuilder(
          'createdAt',
          ReportAggregationType.DATE_HISTOGRAM
        )
          .withInterval(ReportDateHistogramType.DAY)
          .withAggregation(
            new AggregationBuilder(
              'classification.label',
              ReportAggregationType.TERMS
            )
              .withSize(2)
              .withDocCount()
              .build()
          )
          .build()
      )
      .build(),
})

export const getQuery = (queryConfig, options = {}) => {
  const { metric } = queryConfig
  const queriesByMetric = makeQueries({ ...queryConfig, ...options })
  const query = queriesByMetric[metric]

  if (!query) {
    throw Error(
      `no query found for config: ${metric} - ${JSON.stringify(
        queryConfig
      )} with options: ${JSON.stringify(options)}`
    )
  }

  const queryParser = QueryParsersByMetric[metric]

  return queryParser(query())
}

export const RoutingDimensionsToQuery = Object.freeze({
  ON_CALL: 'routingTypeParticipants',
  MANUAL: 'routingTypeParticipants',
  DIRECT: 'routingActionSubTypes',
  PROFILE: 'routingSubTypeParticipants',
  ABM: 'routingSubTypeParticipants',
  LIVE_VIEW: 'routingSubTypeParticipants',
  OUTBOUND: 'routingSubTypeParticipants',
  ROUTING_RULE: 'routingTypeParticipants',
  REROUTE: 'routingTypeParticipants',
  BOT: 'routingTypeParticipants',
  PLAYBOOK_GOAL: 'routingActionPlaybooks', // co-opting this to inject an "playbooks" step
  PLAYBOOK_ACTION: 'routingActionPlaybookActions',
  PLAYBOOK_GOAL_FOR_PLAYBOOK: 'routingActionPlaybookGoals',
  PLAYBOOK_PARTICIPANTS: 'routingTypePlaybookGoalsParticipants',
  PLAYBOOK_ACTION_PARTICIPANTS: 'routingTypePlaybookActionsParticipants',
  ROUTING_RULE_ID: 'routingActionRoutingRuleParticipants',
})
